// Категория: модуль с побочными эффектами (window.gtag, window.LocalConverter, DOM).
//
// Здесь исполнение скачивания: выбор ветки и её запуск. Разбор и подготовка самих
// форматов — в соседнем tools/formats.js, контракты и константы — в
// tools/downloadContracts.js. Файл назывался downloadFormats.js и путался с
// formats.js, хотя форматами не занимается.
import { DOWNLOAD_RESULT, NO_FORMATS_REASON } from './downloadContracts';
import analytics, { CONVERTER_GA_RESOURCE } from './ga';
import { safeFilename, safeUrl } from './sanitize';
import { showConverterUIPopup } from './showConverterUIPopup';

// Дублируется в _playlist-results.scss ($playlist-accent) — SCSS и JS общей константы
// не имеют, поэтому менять цвет нужно в обоих местах.
const CONVERTER_THEME_COLOR = '#ff4900';

// Константы живут в tools/downloadContracts.js: их читает и ga.js, а он не должен
// зависеть от этого модуля — см. комментарий в самом downloadContracts.js.
// Реэкспорта здесь нет намеренно: два пути импорта одной константы противоречат
// идее единственного источника правды.

/**
 * Переводит код возврата startDownload() в причину отказа для аналитики: иначе
 * маппинг DOWNLOAD_RESULT в причину копировался бы в каждом компоненте.
 * @param result {string} - значение DOWNLOAD_RESULT
 * @returns {string|null} значение NO_FORMATS_REASON, либо null если скачивание пошло штатно
 */
function resultToFailureReason(result) {
    if (result === DOWNLOAD_RESULT.NO_CONVERTER) {
        return NO_FORMATS_REASON.NO_CONVERTER;
    }
    if (result === DOWNLOAD_RESULT.BLOCKED) {
        return NO_FORMATS_REASON.BLOCKED;
    }
    if (result === DOWNLOAD_RESULT.FAILED) {
        return NO_FORMATS_REASON.FAILED;
    }
    return null;
}

// Пауза перед открытием прямой ссылки: событие 'download-click' синхронно открывает
// рекламную вкладку, и загрузка файла не должна стартовать в том же тике.
export const DIRECT_DOWNLOAD_DELAY = 500;

// Потолок, сколько кнопка остаётся заблокированной, если клиентский конвертер так и
// не отчитался. Это НЕ таймаут мёржа — прервать его мы не можем и не пытаемся: скрипт
// внешний, у больших видео мёрж идёт долго, и оборвать его значило бы потерять работу.
// Это страховка от того, что отчёта не будет никогда (скрипт завис, заблокирован,
// упал молча): без неё кнопка Download мертва до перезагрузки страницы. Сам мёрж
// после срабатывания продолжается, пользователь просто снова может нажать Download.
const LOCAL_CONVERTER_LOCK_TIMEOUT = 60000;

const settle = (onSettled) => {
    if (onSettled) {
        onSettled();
    }
};

/**
 * Похоже ли значение, которым резолвнулся runConvert(), на отказ.
 * Контракт внешнего скрипта не зафиксирован: часть реализаций сообщает отказ
 * реджектом, часть — резолвом объекта с признаком ошибки. Реджект мы ловим всегда,
 * а здесь покрываем три известные формы резолва; распознать отказ, о котором
 * скрипт сообщил как-то иначе (например, только своим колбэком), мы не можем —
 * это ограничение, а не полная гарантия.
 * @param outcome {*} - значение, которым резолвнулся runConvert()
 * @returns {boolean}
 */
function isConverterFailure(outcome) {
    if (outcome === false) {
        return true;
    }
    if (!outcome || typeof outcome !== 'object') {
        return false;
    }
    return !!outcome.error || outcome.success === false;
}

/**
 * Ветка «модалка конвертера»: скачивание уходит на сторону конвертера, и дальше
 * за прогресс отвечает его окно, а не мы.
 * @returns {string} значение DOWNLOAD_RESULT
 */
function runConverterUiDownload({ url, video, onSettled }) {
    const converterLink = safeUrl(video.url || url);
    if (!converterLink) {
        console.warn('[download] converter link rejected: only http(s) is allowed');
        settle(onSettled);
        return DOWNLOAD_RESULT.BLOCKED;
    }

    analytics.converterLinkClick();

    const clickAdsExperiment = window['clickAds'];
    showConverterUIPopup(
        converterLink,
        CONVERTER_THEME_COLOR,
        // адрес эксперимента тоже открывается через window.open — проверяем схему
        safeUrl(clickAdsExperiment?.url),
        { gaResource: CONVERTER_GA_RESOURCE, adsGaResource: CONVERTER_GA_RESOURCE },
    );
    settle(onSettled);
    return DOWNLOAD_RESULT.CONVERTER_UI;
}

/**
 * Ветка «клиентский мёрж»: формат отдан отдельными потоками, и прямая ссылка
 * здесь дала бы видео без звука.
 * @returns {string} значение DOWNLOAD_RESULT
 */
function runLocalConverterDownload({ video, filename, isAudio, thumb, onLocalConverterError, onSettled }) {
    // скрипт внешний и в репозитории не подключается: он может не загрузиться
    // или быть заблокирован, а путь достижим из каждой карточки выдачи
    const localConverter = window.LocalConverter;
    if (!localConverter || typeof localConverter.runConvert !== 'function') {
        console.warn('[download] LocalConverter is unavailable, this format needs client-side merging');
        settle(onSettled);
        return DOWNLOAD_RESULT.NO_CONVERTER;
    }

    let started;
    try {
        started = localConverter.runConvert({
            thumbnail: safeUrl(thumb),
            streams: video.streams,
            filename: safeFilename(filename),
            mimeType: isAudio ? 'audio/mp3' : 'video/mp4',
            audio: {
                copy: !isAudio,
                format: 'mp3',
                bitrate: '128',
            },
            isHLS: false,
            localProcessingType: isAudio ? 'audio' : 'merge',
            clientName: 'ddown.to',
            metricsSample: 100,
        });
    } catch (e) {
        console.warn('[download] LocalConverter failed to start', e);
        settle(onSettled);
        return DOWNLOAD_RESULT.NO_CONVERTER;
    }

    if (!started || typeof started.then !== 'function') {
        // контракт не обещает промис: момент завершения нам неизвестен,
        // и держать кнопку заблокированной нечем — снимаем блокировку сразу
        settle(onSettled);
        return DOWNLOAD_RESULT.LOCAL_CONVERTER;
    }

    // Блокировку обязаны снять ровно один раз и при ЛЮБОМ исходе, включая тот,
    // где промис не осядет никогда. Иначе isDownloading останется true и кнопка
    // Download умрёт до перезагрузки страницы.
    let released = false;
    const release = () => {
        if (released) {
            return;
        }
        released = true;
        clearTimeout(lockTimer);
        settle(onSettled);
    };
    const lockTimer = setTimeout(() => {
        console.warn('[download] LocalConverter has not reported back, releasing the download button');
        release();
    }, LOCAL_CONVERTER_LOCK_TIMEOUT);

    // отказ приходит уже после возврата из функции — сообщаем вызывающему,
    // иначе он молча останется в состоянии «скачивание пошло»
    const report = (e) => {
        console.warn('[download] LocalConverter failed', e);
        if (onLocalConverterError) {
            onLocalConverterError(e);
        }
    };

    started
        .then((outcome) => {
            if (isConverterFailure(outcome)) {
                report(outcome);
            }
        }, report)
        // сам report() мог бросить: без этого отказ цепочки унёс бы с собой
        // и release(), и превратился бы в unhandled rejection
        .catch((e) => {
            console.warn('[download] LocalConverter reporting failed', e);
        })
        .finally(release);

    return DOWNLOAD_RESULT.LOCAL_CONVERTER;
}

/**
 * Ветка «прямая ссылка».
 * @returns {string} значение DOWNLOAD_RESULT
 */
function runDirectDownload({ url, delay, beforeDirectDownload, onSettled }) {
    const href = safeUrl(url);
    if (!href) {
        console.warn('[download] link rejected: only http(s) is allowed');
        settle(onSettled);
        return DOWNLOAD_RESULT.BLOCKED;
    }

    if (beforeDirectDownload) {
        beforeDirectDownload();
    }

    const openLink = () => {
        const a = document.createElement('a');
        a.href = href;
        a.rel = 'noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    // Блокировку снимаем после того, как ссылка реально открыта: именно в это окно
    // повторный клик успевал запустить вторую загрузку.
    //
    // finally обязателен: колбэк setTimeout выполняется уже вне try/catch диспетчера
    // (тот к этому моменту давно вернул управление), и исключение из openLink()
    // оставило бы кнопку заблокированной до перезагрузки страницы. Это единственная
    // используемая ветка — оба вызывающих передают DIRECT_DOWNLOAD_DELAY.
    const openAndSettle = () => {
        try {
            openLink();
        } catch (e) {
            console.error('[download] failed to open the direct link', e);
        } finally {
            settle(onSettled);
        }
    };

    if (delay > 0) {
        setTimeout(openAndSettle, delay);
    } else {
        openAndSettle();
    }

    return DOWNLOAD_RESULT.DIRECT;
}

/**
 * TEA-6094: единая точка скачивания для одиночной выдачи и карточек выдачи.
 * Диспетчер по форме формата — сами ветки выше.
 *
 * Внутренняя: наружу торчит только startDownloadWithReport() ниже. Прямой вызов
 * в обход обвязки терял бы отчёт об отказе, а отказ здесь приходит двумя путями —
 * кодом возврата и асинхронным колбэком, — и сшивать их каждому вызывающему заново
 * уже приводило к расхождению поведения между компонентами.
 *
 * Всё, что уходит наружу, санитайзится в ветках, а не у вызывающего: ссылки через
 * safeUrl(), имя файла через safeFilename(). Инвариант не должен зависеть от
 * дисциплины каждого вызывающего. Исключение одно — videoObj.streams: их забирает
 * window.LocalConverter, это его граница доверия, и проверить их здесь мы не можем,
 * не зная формата потоков.
 *
 * @param options {Object}
 * @param options.url {string} - прямая ссылка на файл
 * @param options.videoObj {Object} - сырой формат из ответа /api/convert
 * @param options.filename {string} - имя файла для локального конвертера
 * @param options.isAudio {boolean} - формат аудио: влияет на режим локального конвертера
 * @param options.thumb {string} - превью для локального конвертера
 * @param options.delay {number} - задержка перед открытием прямой ссылки, мс
 * @param options.beforeDirectDownload {Function} - вызывается только перед веткой прямой ссылки
 * @param options.onSettled {Function} - скачивание больше ничего не ждёт: для клиентского
 *   мёржа это оседание его промиса либо срабатывание LOCAL_CONVERTER_LOCK_TIMEOUT,
 *   для прямой ссылки — когда она открыта, для остальных веток — сразу.
 *   Вызывается ровно один раз в каждой ветке; на нём держится снятие блокировки кнопки.
 * @param options.onLocalConverterError {Function} - поздний отказ клиентского конвертера.
 *   Вызывается всегда: актуальность попытки решает вызывающий, сравнивая свою ссылку
 *   (PlaylistCard — source, MainResults — link) в reportDownloadFailure. Модульного
 *   счётчика попыток здесь быть не должно: он общий на все карточки выдачи, и
 *   скачивание с соседней карточки глушило бы отказ конвертера на этой (TEA-6094).
 * @returns {string} одно из значений DOWNLOAD_RESULT
 */
function startDownload({
    url,
    videoObj,
    filename,
    isAudio = false,
    thumb,
    delay = 0,
    beforeDirectDownload,
    onLocalConverterError,
    onSettled,
}) {
    const video = videoObj || {};

    // Обёртка делает вызов идемпотентным: ветки зовут settle сами, а catch ниже
    // добирает случай, когда до него не дошло. Дважды снимать блокировку нельзя —
    // вторая попытка разблокировалась бы чужим колбэком.
    let settled = false;
    const settleOnce = () => {
        if (settled) {
            return;
        }
        settled = true;
        settle(onSettled);
    };

    try {
        if (video.isConverterUI) {
            return runConverterUiDownload({ url, video, onSettled: settleOnce });
        }

        if (video.isLocalConverter) {
            return runLocalConverterDownload({
                video, filename, isAudio, thumb, onLocalConverterError, onSettled: settleOnce,
            });
        }

        return runDirectDownload({ url, delay, beforeDirectDownload, onSettled: settleOnce });
    } catch (e) {
        // Любая ветка может бросить синхронно — например, showConverterUIPopup()
        // парсит localStorage без своей защиты, а beforeDirectDownload приходит
        // от вызывающего. Без этого исключение уходило мимо settle, и кнопка
        // Download оставалась заблокированной до перезагрузки страницы.
        console.error('[download] download strategy threw', e);
        settleOnce();
        return DOWNLOAD_RESULT.FAILED;
    }
}

/**
 * Скачивание вместе с отчётом об отказе — общая обвязка для обоих компонентов.
 *
 * startDownload() сообщает об отказе двумя разными путями: синхронно кодом возврата
 * и асинхронно через onLocalConverterError. Раньше каждый компонент сшивал оба пути
 * сам, и различие в том, как они это делают, уже приводило к расхождению поведения.
 *
 * Проверку «попытка ещё актуальна» обвязка НЕ делает намеренно: у компонентов разный
 * критерий (карточка сверяет source, одиночная выдача — link) и разное место вывода
 * ошибки, поэтому это остаётся внутри onFailure.
 *
 * @param options - всё, что принимает startDownload, кроме onLocalConverterError
 * @param options.onFailure {Function} - (reason, suffix) для обоих путей отказа
 * @param options.onSettled {Function} - скачивание больше ничего не ждёт (снять блокировку)
 * @param options.lateFailureSuffix {string} - уточнение для позднего отказа конвертера
 * @returns {string} одно из значений DOWNLOAD_RESULT
 */
export function startDownloadWithReport({ onFailure, lateFailureSuffix, ...options }) {
    const result = startDownload({
        ...options,
        onLocalConverterError: () => onFailure(NO_FORMATS_REASON.NO_CONVERTER, lateFailureSuffix),
    });

    const reason = resultToFailureReason(result);
    if (reason) {
        onFailure(reason);
    }

    return result;
}
